import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3 } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Input } from '@/shared/components/ui/Input';
import { Skeleton } from '@/shared/components/ui/Skeleton';

const REPORTS = [
  { id: 'profit-product', label: 'Profit by Product', endpoint: '/api/reports/operations/profit/by-product' },
  { id: 'profit-category', label: 'Profit by Category', endpoint: '/api/reports/operations/profit/by-category' },
  { id: 'sales-summary', label: 'Sales Summary', endpoint: '/api/reports/operations/sales-summary' },
  { id: 'purchase-summary', label: 'Purchase Summary', endpoint: '/api/reports/operations/purchase-summary' },
  { id: 'stock', label: 'Stock Report', endpoint: '/api/reports/operations/stock' },
  { id: 'commissions', label: 'Commission Report', endpoint: '/api/reports/operations/commissions' },
  { id: 'cash-registers', label: 'Cash Register Report', endpoint: '/api/reports/operations/cash-registers' },
];

/** Turn a snake/camel key into a readable column heading. */
function humanizeKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const MONEY_HINT = /(amount|total|value|price|cost|profit|revenue|sales|purchase|margin|balance|due|tax|gst)/i;

function formatCell(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (MONEY_HINT.test(key)) return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    return value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }
  if (typeof value === 'string') {
    // ISO date → readable date
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('en-IN', { dateStyle: 'medium' });
    }
    return value;
  }
  if (Array.isArray(value)) return `${value.length} item${value.length !== 1 ? 's' : ''}`;
  if (typeof value === 'object') {
    const named = value as { name?: string; title?: string; display_name?: string };
    return named.name ?? named.title ?? named.display_name ?? '—';
  }
  return String(value);
}

/** Pull the first array of records out of an arbitrary report payload. */
function extractRows(payload: unknown): { rows: Record<string, unknown>[]; summary: [string, unknown][] } {
  if (Array.isArray(payload)) return { rows: payload as Record<string, unknown>[], summary: [] };
  if (payload && typeof payload === 'object') {
    const entries = Object.entries(payload as Record<string, unknown>);
    const arrayEntry = entries.find(([, v]) => Array.isArray(v) && v.length > 0 && typeof v[0] === 'object');
    const summary = entries.filter(([, v]) => typeof v === 'number' || typeof v === 'string');
    return {
      rows: arrayEntry ? (arrayEntry[1] as Record<string, unknown>[]) : [],
      summary,
    };
  }
  return { rows: [], summary: [] };
}

export function OperationalReportsPage() {
  const [selected, setSelected] = useState(REPORTS[0]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['ops-reports', selected.id, startDate, endDate],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (startDate) p.append('startDate', startDate);
      if (endDate) p.append('endDate', endDate);
      const { data } = await api.get(`${selected.endpoint}?${p}`);
      return data;
    },
  });

  const { rows, summary } = useMemo(() => extractRows(data), [data]);
  const columns = useMemo(() => {
    if (rows.length === 0) return [];
    const keys = new Set<string>();
    rows.slice(0, 25).forEach((r) => Object.keys(r).forEach((k) => { if (k !== '_id' && k !== '__v') keys.add(k); }));
    return [...keys];
  }, [rows]);

  return (
    <div className="space-y-4 pb-16">
      <PageHeader icon={BarChart3} eyebrow="Insights" title="Operational Reports" description={<>Profit, sales, purchases, stock, commissions and register reports</>} />

      <div className="flex gap-4 items-end flex-wrap">
        <div className="flex gap-2 overflow-x-auto border-b pb-0" role="tablist" aria-label="Report type">
          {REPORTS.map((r) => (
            <button
              key={r.id}
              role="tab"
              aria-selected={selected.id === r.id}
              onClick={() => setSelected(r)}
              className={`px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition ${
                selected.id === r.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center ml-auto">
          <Input aria-label="Start date" name="start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-36" />
          <span className="text-xs text-muted-foreground">to</span>
          <Input aria-label="End date" name="end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-36" />
        </div>
      </div>

      {/* Summary figures returned alongside the rows */}
      {summary.length > 0 && !isLoading && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {summary.map(([key, value]) => (
            <div key={key} className="rounded-xl border bg-card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{humanizeKey(key)}</p>
              <p className="mt-0.5 text-lg font-bold">{formatCell(key, value)}</p>
            </div>
          ))}
        </div>
      )}

      <Card className="shadow-xs">
        <CardHeader title={selected.label} subtitle={rows.length ? `${rows.length} row${rows.length !== 1 ? 's' : ''}` : undefined} />
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-4"><Skeleton className="h-[300px]" /></div>
          ) : isError ? (
            <div className="p-6">
              <EmptyState title="Could not load this report" description="Check your connection or try a different date range." />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={<BarChart3 className="h-8 w-8" aria-hidden />}
                title="No data for this period"
                description="Nothing was recorded in the selected date range. Try widening the dates."
              />
            </div>
          ) : (
            <div className="overflow-auto max-h-[560px]">
              <table className="w-full text-sm text-left">
                <caption className="sr-only">{selected.label}</caption>
                <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                  <tr>
                    {columns.map((c) => (
                      <th key={c} scope="col" className="px-4 py-3 font-medium whitespace-nowrap">{humanizeKey(c)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((row, i) => (
                    <tr key={(row._id as string) ?? i} className="hover:bg-muted/20">
                      {columns.map((c) => {
                        const numeric = typeof row[c] === 'number';
                        return (
                          <td key={c} className={`px-4 py-3 whitespace-nowrap ${numeric ? 'text-right tabular-nums' : ''}`}>
                            {formatCell(c, row[c])}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
